import React, { useState, useEffect } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Button,
  Typography,
  Box,
  Grid,
} from "@mui/material";
import { toast } from "react-toastify";
import OfflinePinIcon from "@mui/icons-material/OfflinePin";
import useAxiosPrivate from "../../common/useAxiosPrivate";
import WebcamCapture from "./WebCamCapture";
import CustomRadioGroup from "../CustomRadioGroup/CustomRadioGroup";
import googleDriveAxios from "../../common/googleDriveAxios";
import CameraCapture from "../CameraCapture/CameraCapture";
import ImageList from "../ImageList/ImageList";

interface ComplianceInspectionChecklistProps {
  chassisNo: string;
  setModel: React.Dispatch<React.SetStateAction<string>>;
  model: string;
  setCheckedBy: React.Dispatch<React.SetStateAction<string>>;
  isDisabled?: boolean;
}

const ComplianceInspectionChecklist: React.FC<
  ComplianceInspectionChecklistProps
> = ({ chassisNo, setModel, model, setCheckedBy, isDisabled }) => {
  const axiosPrivate = useAxiosPrivate();
  const [checklist, setChecklist] = useState<any[]>([]);
  const [checkedBy, setLocalCheckedBy] = useState<string>("");
  const [date, setDate] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(false);
  const [apiError, setApiError] = useState<boolean>(false);
  const [isRadioChanged, setIsRadioChanged] = useState<boolean>(false);
  const [completeStatus, setCompleteStatus] = useState<string>("Incomplete");
  const hasUnsavedChanges = chassisNo;
  const [activeCheckpointList, setActiveCheckpointList] = useState<any[]>([]);

  const checkCheckpointRecord = React.useCallback(async () => {
    if (chassisNo) {
      try {
        const queryParams = new URLSearchParams({
          chassis_no: "DC" + chassisNo,
          dept_name: "compliance-quality-inspection",
        }).toString();

        const response = await googleDriveAxios.post(
          `/checkpoint-image-record?${queryParams}`
        );
        const data = response.data;
        setActiveCheckpointList(data.recorded_checkpoint_list);
        console.log(data.recorded_checkpoint_list);
      } catch (error) {
        console.error("Error checking images:", error);
      }
    }
  }, [chassisNo]);

  useEffect(() => {
    checkCheckpointRecord();
  }, [chassisNo, checkCheckpointRecord]);

  useEffect(() => {
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      if (hasUnsavedChanges) {
        event.preventDefault();
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [hasUnsavedChanges]);

  useEffect(() => {
    const fetchData = async () => {
      if (!chassisNo) {
        setChecklist([]);
        setApiError(false);
        setModel("");
        setCompleteStatus("Incomplete");
        setIsRadioChanged(false);
        return;
      }

      setLoading(true);
      setChecklist([]);
      try {
        const response = await axiosPrivate.post(
          `/prepare_compliance_inspection/${chassisNo}`
        );
        const data = response.data;

        const checklistData =
          data.complianceInspectionChecklist.map(
            (item: any, index: number) => ({
              ...item,
              date:
                index < 2 && item.date
                  ? new Date(item.date).toISOString().split("T")[0]
                  : null,
              status: item.status
                ? String(item.status).trim().toLowerCase()
                : "not checked",
              additionalFields: item.additionalFields || {
                towBall: "",
                passenger: "",
                driver: "",
                total: "",
              },
            })
          ) || [];

        setChecklist(checklistData);
        setModel(data.model || "");
        setLocalCheckedBy(data.checkedBy || "");
        setCheckedBy(data.checkedBy || "");

        let formattedDate = "";
        if (data.createdOn) {
          const date = new Date(data.createdOn);
          if (date.getFullYear() !== 1900) {
            formattedDate = data.createdOn ? data.createdOn.split("T")[0] : "";
          }
        }
        setDate(formattedDate);

        const isComplete =
          checklistData.length > 0 &&
          checklistData.every(
            (item: { status: string }) =>
              item.status &&
              ["pass", "fail", "n/a"].includes(item.status.toLowerCase())
          );
        setCompleteStatus(isComplete ? "Complete" : "Incomplete");
        setIsRadioChanged(false);
      } catch (error) {
        console.error("Error fetching compliance inspection:", error);
        setApiError(true);
        toast.error("Failed to fetch compliance inspection data.");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [axiosPrivate, chassisNo, setModel, setCheckedBy]);

  const handleChecklistChange = (index: number, field: string, value: any) => {
    if (completeStatus === "Complete") return;

    setChecklist((prev) =>
      prev.map((item, i) =>
        i === index
          ? {
              ...item,
              [field]: field === "date" ? value : item[field],
              additionalFields: {
                ...item.additionalFields,
                [field]: value,
                total: String(
                  (Number(
                    field === "towBall" ? value : item.additionalFields.towBall
                  ) || 0) +
                    (Number(
                      field === "passenger"
                        ? value
                        : item.additionalFields.passenger
                    ) || 0) +
                    (Number(
                      field === "driver" ? value : item.additionalFields.driver
                    ) || 0)
                ),
              },
            }
          : item
      )
    );
  };

  const saveData = async () => {
    if (!date.trim()) {
      toast.error("Date is required.");
      return;
    }
    if (!checkedBy.trim()) {
      toast.error("Checked By field is required.");
      return;
    }

    setLoading(true);
    try {
      if (isRadioChanged) {
        const isComplete =
          checklist.length > 0 &&
          checklist.every(
            (item) =>
              item.status &&
              ["pass", "fail", "n/a"].includes(item.status.toLowerCase())
          );
        setCompleteStatus(isComplete ? "Complete" : "Incomplete");
      }

      const payload = {
        chassisNo: chassisNo,
        model: model,
        complianceInspectionChecklist: checklist.map((item) => ({
          ...item,
          date: item.date || null,
          status: String(item.status || "not checked"),
          additionalFields: item.additionalFields || null,
        })),
        checkedBy: checkedBy,
        createdOn: date,
        completeStatus,
      };

      await toast.promise(
        axiosPrivate.post(
          "/create_or_update_compliance_inspection_prep",
          payload
        ),
        {
          pending: "Saving...",
          success: "Saved successfully!",
          error: "Failed to save.",
        }
      );

      setIsRadioChanged(false);

      if (checkedBy) {
        const allChecklists = [...checklist];

        let itemNoCounter = 1;
        const newReworkItems = allChecklists
          .filter((item) => item.status === "fail")
          .flatMap((item) => {
            const title = item.title || "No Title Available";
            const notesStr = item.notes || "";

            if (notesStr.includes(";")) {
              const parts = notesStr
                .split(";")
                .map((part: string) => part.trim())
                .filter((part: any) => part);
              return parts.map((note: any) => ({
                itemNo: itemNoCounter++,
                problemDescription: `${title}: ${note}`,
                comment: "",
                whoFoundFault: checkedBy,
                responsible: "",
                whoDidRework: "",
                timeSpentOnRework: { hours: 0, minutes: 0, seconds: 0 },
                completed: false,
              }));
            } else {
              return [
                {
                  itemNo: itemNoCounter++,
                  problemDescription: `${title}: ${notesStr}`,
                  comment: "",
                  whoFoundFault: checkedBy,
                  responsible: "",
                  whoDidRework: "",
                  timeSpentOnRework: { hours: 0, minutes: 0, seconds: 0 },
                  completed: false,
                },
              ];
            }
          });

        if (newReworkItems.length > 0) {
          const reworkPayload = {
            chassisNo,
            model,
            station_name: "compliance-quality-inspection",
            reworkItems: newReworkItems,
          };

          await axiosPrivate.post(`rework/save`, reworkPayload, {
            params: { rework_type: "compliance-quality-inspection" },
          });
        }
      }
    } catch (error) {
      toast.error("Error saving data.");
    } finally {
      setLoading(false);
    }
  };

  const handleRadioChange = (index: number, value: string) => {
    if (completeStatus === "Complete") return;

    setIsRadioChanged(true);
    const updatedData = [...checklist];
    updatedData[index].status = value.toLowerCase();
    setChecklist(updatedData);
  };

  const handleNotesChange = (index: number, value: string) => {
    if (completeStatus === "Complete") return;

    const updatedData = [...checklist];
    updatedData[index].notes = value;
    setChecklist(updatedData);
  };

  const handleClearFields = (index: number) => {
    if (completeStatus === "Complete") return;

    setChecklist((prev) =>
      prev.map((item, i) =>
        i === index
          ? {
              ...item,
              additionalFields: {
                towBall: "",
                passenger: "",
                driver: "",
                total: "",
              },
            }
          : item
      )
    );
  };

  const allNotChecked =
    checklist.length > 0 &&
    checklist.every((item) => item.status?.toLowerCase() === "not checked");

  console.log("Checklist:", checklist);
  console.log("allNotChecked:", allNotChecked);

  return (
    <Box
      sx={{
        position: "relative",
        filter: loading ? "blur(5px)" : "none",
        pointerEvents: loading ? "none" : "auto",
      }}
    >
      <Typography
        sx={{
          mb: 2,
          fontWeight: "600",
          display: "flex",
          alignItems: "center",
          gap: "5px",
          color:
            completeStatus === "Incomplete" ? "error.main" : "success.main",
        }}
      >
        {completeStatus === "Incomplete" ? (
          <>⚠️ INCOMPLETE</>
        ) : (
          <>
            <OfflinePinIcon /> COMPLETE
          </>
        )}
      </Typography>
      <TableContainer>
        <Table>
          <TableHead>
            <TableRow className="table__head_row">
              <TableCell
                className="table__head_cell"
                sx={{
                  width: "1%",
                  textAlign: "center",
                  verticalAlign: "middle",
                  padding: "0px",
                }}
              ></TableCell>
              <TableCell
                className="table__head_cell"
                sx={{
                  width: "1%",
                  textAlign: "center",
                  verticalAlign: "middle",
                  padding: "0px",
                }}
              >
                S/N
              </TableCell>
              <TableCell className="table__head_cell" sx={{ width: "10%" }}>
                COMPLIANCE CHECK
              </TableCell>
              <TableCell className="table__head_cell" sx={{ width: "15%" }}>
                NOTES
              </TableCell>
              <TableCell
                className="table__head_cell"
                sx={{ width: "5%" }}
                align="center"
              >
                STATUS
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {checklist.map((item, index) => {
              const shouldBold =
                !allNotChecked && item.status?.toLowerCase() === "not checked";
              const serialNumber = index + 1;

              return (
                <TableRow key={index}>
                  <TableCell
                    sx={{
                      width: "30px",
                      textAlign: "center",
                      verticalAlign: "middle",
                      padding: "4px",
                      borderLeft: "1px solid #ccc",
                      borderRight: "1px solid #ccc",
                    }}
                  >
                    <Box sx={{ display: "flex", justifyContent: "center" }}>
                      <CameraCapture
                        chassisDCNumber={"DC" + chassisNo}
                        checkPointName={"checkpoint-" + serialNumber}
                        deptName="compliance-quality-inspection"
                        checkCheckpointRecord={checkCheckpointRecord}
                      />
                      <ImageList
                        chassisDCNumber={"DC" + chassisNo}
                        checkPointName={"checkpoint-" + serialNumber}
                        deptName="compliance-quality-inspection"
                        imagesExist={activeCheckpointList.includes(
                          "checkpoint-" + serialNumber
                        )}
                      />
                    </Box>
                  </TableCell>
                  <TableCell
                    sx={{
                      width: "40px",
                      textAlign: "center",
                      verticalAlign: "middle",
                      padding: "4px",
                      borderLeft: "1px solid #ccc",
                      borderRight: "1px solid #ccc",
                    }}
                  >
                    {serialNumber}
                  </TableCell>
                  <TableCell
                    sx={{
                      borderLeft: "1px solid #d0d0d0",
                      wordWrap: "break-word",
                      whiteSpace: "pre-wrap",
                      verticalAlign: "top",
                      fontWeight: shouldBold ? "bold" : "normal",
                    }}
                  >
                    <Typography
                      sx={{ fontWeight: shouldBold ? "bold" : "normal" }}
                    >
                      {item.title}
                    </Typography>
                    {index < 2 && (
                      <TextField
                        label="Date"
                        type="date"
                        value={item.date || "dd/mm/yyyy"}
                        disabled={completeStatus === "Complete"}
                        onFocus={(e) => {
                          e.target.setAttribute("type", "date");
                          if (item.date === "dd/mm/yyyy") {
                            handleChecklistChange(index, "date", "");
                          }
                        }}
                        onChange={(e) =>
                          handleChecklistChange(index, "date", e.target.value)
                        }
                        size="small"
                        fullWidth
                        InputLabelProps={{ shrink: true }}
                        sx={{
                          width: "60%",
                          marginTop: "8px",
                          fontSize: "14px",
                          fontWeight: shouldBold ? "bold" : "normal",
                        }}
                      />
                    )}
                    {index === 2 && (
                      <Box display="flex" alignItems="center">
                        <Box>
                          {!(completeStatus === "Complete") && (
                            <WebcamCapture
                              onUploadComplete={(result) => {
                                const extractedValues = {
                                  towBall: result.towBall,
                                  passenger: result.passenger,
                                  driver: result.driver,
                                  total: result.total,
                                };

                                if (
                                  extractedValues.towBall === "0" &&
                                  extractedValues.passenger === "0" &&
                                  extractedValues.driver === "0" &&
                                  extractedValues.total === "0"
                                ) {
                                  toast.error(
                                    "No relevant data was found in the captured image. Please try capturing a clearer image."
                                  );
                                } else {
                                  toast.success(
                                    "OCR Text Extraction Complete!"
                                  );
                                }

                                setChecklist((prevChecklist) =>
                                  prevChecklist.map((item, i) =>
                                    i === index
                                      ? {
                                          ...item,
                                          additionalFields: {
                                            ...item.additionalFields,
                                            ...extractedValues,
                                          },
                                        }
                                      : item
                                  )
                                );
                              }}
                            />
                          )}
                          <Button
                            variant="outlined"
                            color="secondary"
                            disabled={completeStatus === "Complete"}
                            onClick={() => handleClearFields(index)}
                            sx={{ mb: 0, ml: 1 }}
                          >
                            Clear
                          </Button>
                        </Box>

                        <Box ml={2}>
                          <TextField
                            label="Tow Ball"
                            value={
                              item.additionalFields?.towBall
                                ? item.additionalFields.towBall
                                : ""
                            }
                            onChange={(e) =>
                              handleChecklistChange(
                                index,
                                "towBall",
                                e.target.value
                              )
                            }
                            size="small"
                            fullWidth
                            disabled={completeStatus === "Complete"}
                            sx={{
                              width: "80%",
                              marginTop: "8px",
                              fontSize: "14px",
                              fontWeight: shouldBold ? "bold" : "normal",
                            }}
                          />
                          <TextField
                            label="Passenger"
                            value={
                              item.additionalFields?.passenger
                                ? item.additionalFields.passenger
                                : ""
                            }
                            onChange={(e) =>
                              handleChecklistChange(
                                index,
                                "passenger",
                                e.target.value
                              )
                            }
                            size="small"
                            fullWidth
                            disabled={completeStatus === "Complete"}
                            sx={{
                              width: "80%",
                              marginTop: "8px",
                              fontSize: "14px",
                              fontWeight: shouldBold ? "bold" : "normal",
                            }}
                          />
                          <TextField
                            label="Driver"
                            value={
                              item.additionalFields?.driver
                                ? item.additionalFields.driver
                                : ""
                            }
                            onChange={(e) =>
                              handleChecklistChange(
                                index,
                                "driver",
                                e.target.value
                              )
                            }
                            size="small"
                            fullWidth
                            disabled={completeStatus === "Complete"}
                            sx={{
                              width: "80%",
                              marginTop: "8px",
                              fontSize: "14px",
                              fontWeight: shouldBold ? "bold" : "normal",
                            }}
                          />
                          <TextField
                            label="Total"
                            value={
                              item.additionalFields?.total
                                ? item.additionalFields.total + " Kg"
                                : ""
                            }
                            disabled
                            size="small"
                            fullWidth
                            sx={{
                              width: "80%",
                              marginTop: "8px",
                              fontSize: "14px",
                              marginBottom: "8px",
                              backgroundColor: "#f2f0ef",
                              fontWeight: shouldBold ? "bold" : "normal",
                            }}
                          />
                          {item.additionalFields?.passenger &&
                            item.additionalFields?.driver &&
                            item.additionalFields?.towBall &&
                            item.additionalFields?.total &&
                            Number(item.additionalFields.passenger) +
                              Number(item.additionalFields.driver) +
                              Number(item.additionalFields.towBall) !==
                              Number(item.additionalFields.total) && (
                              <Typography
                                variant="body2"
                                color="error"
                                sx={{
                                  marginTop: "8px",
                                  fontSize: "14px",
                                  fontWeight: shouldBold ? "bold" : "normal",
                                }}
                              >
                                There seems to be a mismatch between the
                                extracted total and the sum of the values.
                                Kindly reupload the image to resolve this issue.
                              </Typography>
                            )}
                        </Box>
                      </Box>
                    )}
                  </TableCell>
                  <TableCell
                    sx={{
                      borderLeft: "1px solid #d0d0d0",
                      wordWrap: "break-word",
                      whiteSpace: "pre-wrap",
                      verticalAlign: "top",
                      fontWeight: shouldBold ? "bold" : "normal",
                    }}
                  >
                    <TextField
                      placeholder="Type the note here..."
                      variant="standard"
                      value={item.notes}
                      onChange={(e) => handleNotesChange(index, e.target.value)}
                      size="small"
                      fullWidth
                      multiline
                      disabled={completeStatus === "Complete"}
                      InputProps={{
                        disableUnderline: true,
                        style: {
                          fontSize: "14px",
                          fontWeight: shouldBold ? "bold" : "normal",
                        },
                      }}
                      sx={{
                        "& .MuiInputBase-input": {
                          padding: 0,
                        },
                        "& .MuiInputBase-root": {
                          border: "none",
                          backgroundColor: "transparent",
                        },
                      }}
                    />
                  </TableCell>
                  <TableCell
                    className="p-0 px-2"
                    sx={{
                      borderLeft: "1px solid #d0d0d0",
                      borderRight: "1px solid #d0d0d0",
                      textAlign: "center",
                      verticalAlign: "center",
                      fontWeight: shouldBold ? "bold" : "normal",
                    }}
                  >
                    <CustomRadioGroup
                      value={item.status}
                      onChange={(value) => handleRadioChange(index, value)}
                      options={[
                        { label: "Pass", value: "Pass" },
                        { label: "Not Checked", value: "Not Checked" },
                        { label: "Fail", value: "Fail" },
                        { label: "N/A", value: "N/A" },
                      ]}
                      disabled={completeStatus === "Complete"}
                      shouldBold={shouldBold}
                    />
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
      <Grid container spacing={3} my={4}>
        <Grid item md={4}>
          <TextField
            label="Checked By"
            value={checkedBy}
            onChange={(e) => {
              if (completeStatus === "Complete") return;
              setLocalCheckedBy(e.target.value);
              setCheckedBy(e.target.value);
            }}
            fullWidth
            hidden={apiError}
            disabled={completeStatus === "Complete"}
          />
        </Grid>
        <Grid item md={4}>
          <TextField
            label="Date"
            type="date"
            value={date}
            onChange={(e) => {
              if (completeStatus === "Complete") return;
              setDate(e.target.value);
            }}
            fullWidth
            InputLabelProps={{ shrink: true }}
            hidden={apiError}
            disabled={completeStatus === "Complete"}
          />
        </Grid>
      </Grid>

      <Button
        variant="contained"
        color="primary"
        onClick={saveData}
        disabled={
          checklist.length === 0 ||
          apiError ||
          loading ||
          (!chassisNo && !model) ||
          completeStatus === "Complete"
        }
      >
        Save
      </Button>
    </Box>
  );
};

export default ComplianceInspectionChecklist;
